import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DeviceModelFeature, intentIsBound,
  getDeviceModels,
  createDeviceModel,
  updateDeviceModel,
  deleteDeviceModel,
  getButtonFunctions,
  getLedOutputs,
  DeviceModel,
  DeviceModelInput,
  DeviceModelButtonIntent,
  ButtonFunction,
  LedOutput,
} from '../../services/admin';
import ButtonFunctionsManager from './ButtonFunctionsManager';
import LedOutputsManager from './LedOutputsManager';
import LedMappingsEditor from './LedMappingsEditor';
import ModelFeaturesEditor from './ModelFeaturesEditor';
import { featurePayload, toFeatureRows } from './modelFeatures';
import { prepareLedMappings, toLedMappingRows, type LedMappingRow } from './ledOutputs';
import ApkRepositoryManager from './ApkRepositoryManager';
import { activateDeviceModel, applyProfileBindings, DiscoveryApiError } from '../../services/modelDiscovery';
// Lazy: the WebUSB/ADB code loads only when an admin opens Model Discovery.
const ModelDiscovery = React.lazy(() => import('./ModelDiscovery'));

const DEVICE_TYPE_OPTIONS: { value: DeviceModel['device_type']; label: string }[] = [
  { value: 'radio', label: 'Radio' },
  { value: 'bodyworn', label: 'Bodyworn' },
];

// Key behaviour a button action's intent fires for - fixed list, but an existing intent saved
// with some other value (e.g. legacy free-text data) is still shown as its own option so editing
// a model never silently discards/changes it.
const KEY_BEHAVIOR_OPTIONS = ['press', 'release', 'short-press', 'long press', 'clockwise', 'counterclockwise'];

const emptyForm: DeviceModelInput = {
  model_name: '',
  device_type: 'radio',
  screen_size: '',
  touch_screen: false,
  keypad: false,
  button_actions: [],
  led_mappings: [],
  features: [],
  default_registration_url: 'http://172.20.21.94:8080',
};

/** Model Catalog (Settings > Model Catalog) - per-hardware-model settings, global across all
 * networks. Rows are auto-created (bare defaults) the first time a not-yet-seen Android
 * Build.MODEL enrolls (see documentation/protocol.md) - flagged here so an admin knows which ones still
 * need review. */
// Model Catalog subpages (/Settings/models/<key>), each reached from a button on the front page.
const SUBPAGES = {
  buttons: { label: '🔘 Buttons', render: () => <ButtonFunctionsManager /> },
  leds: { label: '💡 LED', render: () => <LedOutputsManager /> },
  discover: { label: '🔎 Model Discovery', render: () => <React.Suspense fallback={<div className="text-gray-400">Loading Model Discovery…</div>}><ModelDiscovery /></React.Suspense> },
} as const;
type Subpage = keyof typeof SUBPAGES;

export default function ModelCatalogManager() {
  const { subsection } = useParams<{ subsection?: string }>();
  const navigate = useNavigate();
  const [models, setModels] = useState<DeviceModel[]>([]);
  const [buttonFunctions, setButtonFunctions] = useState<ButtonFunction[]>([]);
  const [ledOutputs, setLedOutputs] = useState<LedOutput[]>([]);
  // The editor's LEDs rows (output + intent), turned into led_mappings on save.
  const [ledRows, setLedRows] = useState<LedMappingRow[]>([]);
  // The editor's Supported Features (application feature capabilities).
  const [featureRows, setFeatureRows] = useState<DeviceModelFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<DeviceModelInput>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      setModels(await getDeviceModels());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load model catalog');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    getButtonFunctions().then(setButtonFunctions).catch(() => {});
    getLedOutputs().then(setLedOutputs).catch(() => {});
    const interval = setInterval(() => {
      if (!showForm) load(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [load, showForm]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setLedRows([]);
    // A new model starts with every feature supported (the registry comes from any listed model).
    setFeatureRows(toFeatureRows(models[0]?.features).map((f) => ({ ...f, supported: true })));
    setError(null);
    getLedOutputs().then(setLedOutputs).catch(() => {});
    setShowForm(true);
  };

  const openEdit = (m: DeviceModel) => {
    setEditingId(m.id);
    setForm({
      model_name: m.model_name,
      device_type: m.device_type,
      screen_size: m.screen_size ?? '',
      touch_screen: m.touch_screen,
      keypad: m.keypad,
      button_actions: (m.button_actions ?? []).map((ba) => ({ ...ba, intents: ba.intents ?? [] })),
      led_mappings: m.led_mappings ?? [],
      features: m.features ?? [],
      default_registration_url: m.default_registration_url,
      android_manufacturer: m.android_manufacturer ?? '',
      android_device: m.android_device ?? '',
    });
    setLedRows(toLedMappingRows(m.led_mappings));
    setFeatureRows(toFeatureRows(m.features));
    setError(null);
    getLedOutputs().then(setLedOutputs).catch(() => {});
    setShowForm(true);
  };

  const addButtonAction = () => {
    setForm({ ...form, button_actions: [...form.button_actions, { button_function_id: '', intents: [], user_configurable: false }] });
  };

  const setButtonActionFunction = (index: number, buttonFunctionId: string) => {
    setForm({
      ...form,
      button_actions: form.button_actions.map((ba, i) =>
        i === index ? { ...ba, button_function_id: buttonFunctionId, intents: [] } : ba
      ),
    });
  };

  const setButtonActionUserConfigurable = (index: number, userConfigurable: boolean) => {
    setForm({
      ...form,
      button_actions: form.button_actions.map((ba, i) =>
        i === index ? { ...ba, user_configurable: userConfigurable } : ba
      ),
    });
  };

  const addButtonActionIntent = (index: number) => {
    setForm({
      ...form,
      button_actions: form.button_actions.map((ba, i) =>
        i === index ? { ...ba, intents: [...ba.intents, { id: '', name: '', action: '' }] } : ba
      ),
    });
  };

  const updateButtonActionIntent = (index: number, intentIndex: number, patch: Partial<DeviceModelButtonIntent>) => {
    setForm({
      ...form,
      button_actions: form.button_actions.map((ba, i) =>
        i === index
          ? { ...ba, intents: ba.intents.map((intent, j) => (j === intentIndex ? { ...intent, ...patch } : intent)) }
          : ba
      ),
    });
  };

  const removeButtonActionIntent = (index: number, intentIndex: number) => {
    setForm({
      ...form,
      button_actions: form.button_actions.map((ba, i) =>
        i === index ? { ...ba, intents: ba.intents.filter((_, j) => j !== intentIndex) } : ba
      ),
    });
  };

  const removeButtonAction = (index: number) => {
    setForm({ ...form, button_actions: form.button_actions.filter((_, i) => i !== index) });
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const leds = prepareLedMappings(ledRows, ledOutputs);
    if ('error' in leds) {
      setError(leds.error);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: DeviceModelInput = {
        ...form,
        led_mappings: leds.mappings,
        features: featurePayload(featureRows),
        button_actions: form.button_actions
          .filter((ba) => ba.button_function_id)
          .map((ba) => ({ ...ba, intents: ba.intents.filter((intent) => intent.name.trim() && intentIsBound(intent)) }))
          .filter((ba) => ba.intents.length > 0),
      };
      if (editingId) {
        await updateDeviceModel(editingId, payload);
      } else {
        await createDeviceModel(payload);
      }
      closeForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save model');
    } finally {
      setSaving(false);
    }
  };

  // A Model Discovery draft becomes ACTIVE (USB-installable once a trusted APK exists) only after
  // the backend re-validates its device profile.
  const handleActivate = async (m: DeviceModel) => {
    if (!confirm(`Activate the draft model "${m.model_name}"? Settings > Deployment will then recognise it (installation still needs a trusted APK for it).`)) return;
    try {
      await activateDeviceModel(m.id);
      await load();
    } catch (err) {
      const v = err instanceof DiscoveryApiError ? err.validation : undefined;
      setError(v ? v.errors.map((e) => `${e.code}: ${e.message}`).join(' ') : err instanceof Error ? err.message : 'Activation failed');
    }
  };

  // Regenerates a DRAFT's button bindings from its own reviewed device profile (the validated
  // Model Discovery -> Model Catalog path); the backend refuses ACTIVE models.
  const handleApplyBindings = async (m: DeviceModel) => {
    if (!confirm(`Replace the button bindings of the draft "${m.model_name}" with the ones discovered in its device profile?`)) return;
    try {
      const r = await applyProfileBindings(m.id);
      await load();
      if (r.notes.length > 0) alert(`Bindings applied (${r.button_actions.length} buttons).\n\n${r.notes.join('\n')}`);
    } catch (err) {
      const v = err instanceof DiscoveryApiError ? err.validation : undefined;
      setError(v ? v.errors.map((e) => `${e.code}: ${e.message}`).join(' ') : err instanceof Error ? err.message : 'Applying bindings failed');
    }
  };

  const handleDelete = async (m: DeviceModel) => {
    if (!confirm(`Delete the "${m.model_name}" entry from the Model Catalog?`)) return;
    try {
      await deleteDeviceModel(m.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete model');
    }
  };

  if (subsection && subsection in SUBPAGES) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/Settings/models')}
          className="text-blue-400 hover:text-blue-300 font-semibold"
        >
          ← Model Catalog
        </button>
        {SUBPAGES[subsection as Subpage].render()}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">📱 Model Catalog</h2>
          <p className="text-gray-400 text-sm mt-1">Per-hardware-model settings, shared across every network.</p>
        </div>
        <div className="flex gap-2">
          {(Object.keys(SUBPAGES) as Subpage[]).map((key) => (
            <button
              key={key}
              onClick={() => navigate(`/Settings/models/${key}`)}
              className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
            >
              {SUBPAGES[key].label}
            </button>
          ))}
          <button
            onClick={openAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            + Add Model
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg p-3">{error}</div>
      )}

      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-900/60 text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left">Model</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Screen</th>
              <th className="px-4 py-3 text-left">Touch</th>
              <th className="px-4 py-3 text-left">Keypad</th>
              <th className="px-4 py-3 text-left">Registration URL</th>
              <th className="px-4 py-3 text-left"></th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {models.map((m) => (
              <tr key={m.id} className="hover:bg-gray-700/30">
                <td className="px-4 py-3 font-mono font-semibold">{m.model_name}</td>
                <td className="px-4 py-3 capitalize">{m.device_type}</td>
                <td className="px-4 py-3">{m.screen_size || '—'}</td>
                <td className="px-4 py-3">{m.touch_screen ? '✓' : '—'}</td>
                <td className="px-4 py-3">{m.keypad ? '✓' : '—'}</td>
                <td className="px-4 py-3 font-mono text-xs text-gray-400">{m.default_registration_url}</td>
                <td className="px-4 py-3">
                  {m.auto_created && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold border capitalize bg-yellow-500/20 text-yellow-400 border-yellow-500">
                      Needs review
                    </span>
                  )}
                  {m.lifecycle_state === 'DRAFT' && (
                    <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-bold border bg-purple-500/20 text-purple-300 border-purple-500" title="Created by Model Discovery; not USB-installable until activated">
                      DRAFT
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {m.lifecycle_state === 'DRAFT' && (
                    <>
                      <button onClick={() => handleApplyBindings(m)} className="text-purple-300 hover:text-purple-200 font-semibold mr-3" title="Bind the buttons discovered in this draft's device profile">Apply discovered bindings</button>
                      <button onClick={() => handleActivate(m)} className="text-purple-300 hover:text-purple-200 font-semibold mr-3">Activate</button>
                    </>
                  )}
                  <button onClick={() => openEdit(m)} className="text-blue-400 hover:text-blue-300 font-semibold mr-3">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(m)} className="text-red-400 hover:text-red-300 font-semibold">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {models.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  No models yet - they're added automatically the first time a radio of that model enrolls, or add one manually.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-700 pt-6">
        <ApkRepositoryManager models={models} />
      </div>

      {showForm && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <h3 className="text-2xl font-bold text-white">{editingId ? 'Edit Model' : 'Add Model'}</h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <p className="text-sm font-semibold text-gray-300 mb-1">Model Name</p>
                  <input
                    type="text"
                    value={form.model_name}
                    onChange={(e) => setForm({ ...form, model_name: e.target.value })}
                    disabled={!!editingId}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="e.g., S380"
                    required
                  />
                  {editingId && <p className="text-xs text-gray-500 mt-1">Must match Android's Build.MODEL exactly - cannot be changed after creation.</p>}
                </label>

                <label className="block">
                  <p className="text-sm font-semibold text-gray-300 mb-1">Device Type</p>
                  <select
                    value={form.device_type}
                    onChange={(e) => setForm({ ...form, device_type: e.target.value as DeviceModel['device_type'] })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    {DEVICE_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <p className="text-sm font-semibold text-gray-300 mb-1">Screen Size</p>
                  <input
                    type="text"
                    value={form.screen_size ?? ''}
                    onChange={(e) => setForm({ ...form, screen_size: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder={'e.g., 3.5"'}
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-detected from the device's reported display metrics on first enrollment - edit if it's missing or wrong.</p>
                </label>

                {editingId && (
                  <div className="grid grid-cols-2 gap-3" data-usb-hardware-identity>
                    <label className="block">
                      <p className="text-sm font-semibold text-gray-300 mb-1">Android manufacturer (USB)</p>
                      <input
                        type="text"
                        value={form.android_manufacturer ?? ''}
                        onChange={(e) => setForm({ ...form, android_manufacturer: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white font-mono"
                        placeholder="ro.product.manufacturer"
                      />
                    </label>
                    <label className="block">
                      <p className="text-sm font-semibold text-gray-300 mb-1">Android device (USB)</p>
                      <input
                        type="text"
                        value={form.android_device ?? ''}
                        onChange={(e) => setForm({ ...form, android_device: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white font-mono"
                        placeholder="ro.product.device"
                      />
                    </label>
                    <p className="col-span-2 text-xs text-gray-500 -mt-1">Exactly what a physical unit of this model reports (read it in Settings &gt; Deployment). Deployment only installs on a radio whose model, manufacturer and device all match; leave empty to disable USB installation for this model.</p>
                  </div>
                )}

                <label className="block">
                  <p className="text-sm font-semibold text-gray-300 mb-1">Default URL for Registration</p>
                  <input
                    type="text"
                    value={form.default_registration_url}
                    onChange={(e) => setForm({ ...form, default_registration_url: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent font-mono"
                    placeholder="http://172.20.21.94:8080"
                  />
                </label>
              </div>

              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input
                    type="checkbox"
                    checked={form.touch_screen}
                    onChange={(e) => setForm({ ...form, touch_screen: e.target.checked })}
                  />
                  Touch Screen
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input
                    type="checkbox"
                    checked={form.keypad}
                    onChange={(e) => setForm({ ...form, keypad: e.target.checked })}
                  />
                  Keypad
                </label>
              </div>

              <ModelFeaturesEditor rows={featureRows} onChange={setFeatureRows} />

              <div className="border-t border-gray-700 pt-4 space-y-3">
                <p className="text-sm font-semibold text-gray-300">Hardware Button Broadcast Actions</p>
                <p className="text-xs text-gray-500 -mt-2">Attach a button from the Button Functions catalog, then define the intents this model's hardware fires for it (e.g. "press", "release") along with the Android intent action string for each (see HardwareKeyReceiver.kt) - varies by OEM/model, so intents are defined per model.</p>
                <div className="space-y-3">
                  {form.button_actions.map((ba, index) => (
                    <div key={index} className="bg-gray-900/50 border border-gray-700 rounded p-3 space-y-2">
                      <div className="flex gap-2 items-start">
                        <select
                          value={ba.button_function_id}
                          onChange={(e) => setButtonActionFunction(index, e.target.value)}
                          className="w-40 shrink-0 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                        >
                          <option value="">Select button…</option>
                          {buttonFunctions.map((f) => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                          ))}
                        </select>
                        <label className="flex items-center gap-1.5 text-xs text-gray-300 shrink-0 whitespace-nowrap self-center">
                          <input
                            type="checkbox"
                            checked={ba.user_configurable}
                            onChange={(e) => setButtonActionUserConfigurable(index, e.target.checked)}
                          />
                          User-changeable
                        </label>
                        <button
                          type="button"
                          onClick={() => removeButtonAction(index)}
                          className="ml-auto px-3 py-2 text-red-400 hover:text-red-300 font-semibold text-sm"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="space-y-2 pl-2 border-l-2 border-gray-700">
                        {ba.intents.map((intent, intentIndex) => (
                          <div key={intentIndex} className="flex gap-2 items-start">
                            <select
                              value={intent.name}
                              onChange={(e) => updateButtonActionIntent(index, intentIndex, { name: e.target.value })}
                              className="w-32 shrink-0 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-600 focus:border-transparent text-xs"
                            >
                              <option value="">Select…</option>
                              {KEY_BEHAVIOR_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                              {intent.name && !KEY_BEHAVIOR_OPTIONS.includes(intent.name) && (
                                <option value={intent.name}>{intent.name}</option>
                              )}
                            </select>
                            {intent.key_code != null ? (
                              <span
                                className="flex-1 px-3 py-2 bg-gray-900/60 border border-gray-700 rounded text-gray-300 font-mono text-xs"
                                title="Android KeyEvent binding (from Model Discovery); matched on key code AND scan code"
                              >
                                KeyEvent keyCode {intent.key_code} / scanCode {intent.scan_code}
                              </span>
                            ) : (
                              <input
                                type="text"
                                value={intent.action}
                                onChange={(e) => updateButtonActionIntent(index, intentIndex, { action: e.target.value })}
                                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent font-mono text-xs"
                                placeholder="android.intent.action.PTT"
                              />
                            )}
                            <button
                              type="button"
                              onClick={() => removeButtonActionIntent(index, intentIndex)}
                              className="px-3 py-2 text-red-400 hover:text-red-300 font-semibold text-xs"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        {ba.intents.length === 0 && (
                          <p className="text-xs text-gray-500 italic">No intents added yet.</p>
                        )}
                        <button
                          type="button"
                          onClick={() => addButtonActionIntent(index)}
                          className="text-xs text-blue-400 hover:text-blue-300 font-semibold"
                        >
                          + Add Intent
                        </button>
                      </div>
                    </div>
                  ))}
                  {form.button_actions.length === 0 && (
                    <p className="text-xs text-gray-500 italic">No button actions added yet.</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={addButtonAction}
                  className="text-sm text-blue-400 hover:text-blue-300 font-semibold"
                >
                  + Add Button Action
                </button>
              </div>

              <LedMappingsEditor rows={ledRows} outputs={ledOutputs} onChange={setLedRows} />

              {error && (
                <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg p-3 text-sm">{error}</div>
              )}

              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
